import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

const CookiePolicyPage = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 2, 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-amber-50" data-testid="cookie-policy-page">
      {/* Header - Transparent Style */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/images/logo-main.png" 
              alt="RecoveryMatch" 
              className="w-10 h-10 rounded-xl"
            />
            <span className="font-bold text-xl">
              <span className="text-[#1C3A7A]">Recovery</span>
              <span className="text-[#FAB710]">Match</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-[#1C3A7A] transition-colors">Features</button>
            <button onClick={() => navigate('/founder')} className="hover:text-[#1C3A7A] transition-colors">About</button>
            <button onClick={() => navigate('/faq')} className="hover:text-[#1C3A7A] transition-colors">FAQ</button>
            <button onClick={() => navigate('/contact')} className="hover:text-[#1C3A7A] transition-colors">Contact</button>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/login')}
              className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition px-4 py-2 hidden sm:block"
            >
              Log In
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="bg-[#FAB710] text-white px-5 py-2.5 rounded-full font-semibold hover:shadow-lg hover:shadow-orange-200 hover:scale-105 transition-all duration-300"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pt-28 pb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1C3A7A] mb-2">Cookie Policy</h1>
        <p className="text-gray-500 mb-8">Last Updated: {lastUpdated}</p>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 space-y-8 text-gray-700 leading-relaxed">
          
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">1. Introduction</h2>
            <p>
              This Cookie Policy explains how RecoveryMatch ("we," "our," or "us") uses cookies and similar 
              technologies when you visit our website or use our mobile application (collectively, the "Service").
            </p>
            <p className="mt-3">
              By using our Service, you consent to the use of cookies as described in this policy. You can 
              manage your cookie preferences at any time through your browser settings or our cookie consent tool.
            </p>
          </section>

          {/* What Are Cookies */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">2. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) 
              when you visit a website. They help the website recognize your device and remember information 
              about your visit, such as your preferences and settings.
            </p>
            <p className="mt-3">
              Cookies are widely used to make websites work more efficiently and to provide information to 
              website owners about how users interact with their sites.
            </p>
          </section>

          {/* Types of Cookies */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">3. Types of Cookies We Use</h2>
            
            <div className="mt-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1C3A7A] mb-2">Essential Cookies</h3>
                <p className="text-sm">
                  These cookies are necessary for the Service to function properly. They enable core functionality 
                  such as security, network management, and account authentication. You cannot opt out of these 
                  cookies as the Service would not work without them.
                </p>
                <ul className="list-disc list-inside mt-2 text-sm space-y-1">
                  <li>Authentication and login status</li>
                  <li>Security tokens</li>
                  <li>Session management</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1C3A7A] mb-2">Functional Cookies</h3>
                <p className="text-sm">
                  These cookies allow us to remember choices you make and provide enhanced, personalized features. 
                  They may be set by us or by third-party providers whose services we have added to our pages.
                </p>
                <ul className="list-disc list-inside mt-2 text-sm space-y-1">
                  <li>Language preferences</li>
                  <li>Location settings</li>
                  <li>Personalized content</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1C3A7A] mb-2">Analytics Cookies</h3>
                <p className="text-sm">
                  These cookies help us understand how visitors interact with our Service by collecting and 
                  reporting information anonymously. This helps us improve how our Service works.
                </p>
                <ul className="list-disc list-inside mt-2 text-sm space-y-1">
                  <li>Pages visited and time spent</li>
                  <li>Features used</li>
                  <li>Error reports</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1C3A7A] mb-2">Advertising Cookies</h3>
                <p className="text-sm">
                  These cookies may be set through our Service by our advertising partners. They may be used 
                  to build a profile of your interests and show you relevant advertisements on other sites.
                </p>
                <ul className="list-disc list-inside mt-2 text-sm space-y-1">
                  <li>Interest-based advertising</li>
                  <li>Campaign effectiveness measurement</li>
                  <li>Ad frequency limiting</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Third-Party Cookies */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">4. Third-Party Cookies</h2>
            <p>
              Some cookies are placed by third-party services that appear on our pages. We do not control 
              the use of these cookies and cannot access them. The third parties that set these cookies can 
              recognize your device both when it visits our Service and when it visits certain other websites.
            </p>
            <p className="mt-3">Third-party services we may use include:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>Google Analytics</strong> - for website analytics</li>
              <li><strong>Firebase</strong> - for app analytics and crash reporting</li>
              <li><strong>Facebook/Meta</strong> - for social features and advertising</li>
              <li><strong>Stripe</strong> - for payment processing</li>
            </ul>
          </section>

          {/* Cookie Duration */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">5. Cookie Duration</h2>
            <p>Cookies can remain on your device for different periods of time:</p>
            <ul className="list-disc list-inside mt-3 space-y-2">
              <li>
                <strong>Session Cookies:</strong> These are temporary cookies that are deleted when you close 
                your browser. They are used to maintain your session while you navigate the Service.
              </li>
              <li>
                <strong>Persistent Cookies:</strong> These cookies remain on your device for a set period or 
                until you delete them. They are used to remember your preferences for future visits.
              </li>
            </ul>
          </section>

          {/* Managing Cookies */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">6. Managing Your Cookie Preferences</h2>
            <p>
              You have several options for managing cookies:
            </p>
            
            <div className="mt-4 space-y-3">
              <div>
                <h3 className="font-semibold text-[#1C3A7A]">Browser Settings</h3>
                <p className="text-sm mt-1">
                  Most web browsers allow you to control cookies through their settings. You can set your 
                  browser to refuse cookies, delete cookies, or alert you when a cookie is being sent.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold text-[#1C3A7A]">Mobile Device Settings</h3>
                <p className="text-sm mt-1">
                  Your mobile device may allow you to limit the use of advertising identifiers. Check your 
                  device's privacy settings for options like "Limit Ad Tracking" (iOS) or "Opt out of Ads 
                  Personalization" (Android).
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-[#1C3A7A]">Opt-Out Links</h3>
                <p className="text-sm mt-1">
                  You can opt out of interest-based advertising by visiting:
                </p>
                <ul className="list-disc list-inside text-sm mt-1">
                  <li>Digital Advertising Alliance: <a href="https://optout.aboutads.info" className="text-[#FAB710] hover:underline" target="_blank" rel="noopener noreferrer">optout.aboutads.info</a></li>
                  <li>Network Advertising Initiative: <a href="https://optout.networkadvertising.org" className="text-[#FAB710] hover:underline" target="_blank" rel="noopener noreferrer">optout.networkadvertising.org</a></li>
                </ul>
              </div>
            </div>

            <div className="mt-4 p-4 bg-[#FAB710]/10 rounded-lg border border-[#FAB710]/20">
              <p className="text-sm">
                <strong>Note:</strong> If you disable or refuse cookies, some parts of the Service may become 
                inaccessible or not function properly. Essential cookies cannot be disabled as they are 
                required for the Service to operate.
              </p>
            </div>
          </section>

          {/* Do Not Track */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">7. Do Not Track Signals</h2>
            <p>
              Some browsers include a "Do Not Track" (DNT) feature that signals to websites that you do not 
              want to have your online activity tracked. Currently, there is no uniform standard for how 
              websites should respond to DNT signals.
            </p>
            <p className="mt-3">
              At this time, we do not respond to DNT signals. However, you can manage your tracking preferences 
              using the methods described in Section 6 above.
            </p>
          </section>

          {/* Updates */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">8. Updates to This Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in our practices or for 
              other operational, legal, or regulatory reasons. We will notify you of any material changes by 
              posting the updated policy on our Service with a new "Last Updated" date.
            </p>
            <p className="mt-3">
              We encourage you to review this Cookie Policy periodically to stay informed about our use of cookies.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-bold text-[#1C3A7A] mb-3">9. Contact Us</h2>
            <p>
              If you have any questions about our use of cookies or this Cookie Policy, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold text-[#1C3A7A]">RecoveryMatch Privacy Team</p>
              <p className="text-sm mt-1">Email: support@recoverymatch.com</p>
              <p className="text-sm">Address: 123 Recovery Lane, Suite 100, Austin, TX 78701</p>
            </div>
          </section>

        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button 
            onClick={() => navigate("/")}
            className="text-[#1C3A7A] hover:text-[#FAB710] font-medium transition"
          >
            ← Back to Home
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
